import { Link } from 'react-router-dom';
import { useRuns } from '@/features/mapari/api/runHooks';
import { runActualCostEur } from '@/features/mapari/lib/timing';
import { useWallet } from '@/lib/walletHooks';
import { formatCurrencyEurRo } from '@/lib/format';

/**
 * `1 h 30 min` / `12 min 30 s` / `45 s` — no formatted-duration helper exists
 * yet (`deriveTiming` in `features/mapari/lib/timing.ts` returns a raw ms
 * number and is typed against the full camelCase `RunDetail`, not the
 * snake_case `RunSummary` this table works from), so a small inline
 * formatter is used here instead, per the task brief. Built from the run's
 * own `started_at`/`finished_at` — never `Date.now()`.
 */
function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h} h ${m} min`;
  if (m > 0) return `${m} min ${s} s`;
  return `${s} s`;
}

export function CostPerMapare() {
  const { data: runs, isPending, isError } = useRuns(undefined, 20);
  const { data: wallet, isPending: walletPending } = useWallet();

  if (isPending) return <div className="h-40 animate-pulse rounded-2xl bg-wash" aria-hidden />;
  if (isError || !runs) return <p className="text-[13.5px] text-bad">Nu s-au putut încărca mapările.</p>;

  const settled = runs.filter((r) => r.started_at && r.finished_at);
  if (settled.length === 0) return <p className="text-[13.5px] text-ink-3">Nicio mapare încă.</p>;

  return (
    <div>
      <p className="text-[13.5px] text-ink-3">
        Cost de calcul per mapare — durata rulării înmulțită cu tariful pe oră al planului. Este un cost de
        procesare estimat pe mapare, diferit de suma reținută efectiv din portofel (facturată pe sesiune de
        instanță, vezi „Sesiuni instanță" mai sus).
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[480px] text-[13.5px]">
          <thead>
            <tr className="border-b border-rule text-left text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3">
              <th className="px-5 py-2.5 font-bold sm:px-6">Fișier</th>
              <th className="px-5 py-2.5 text-right font-bold sm:px-6">Durată</th>
              <th className="px-5 py-2.5 text-right font-bold sm:px-6">Cost</th>
            </tr>
          </thead>
          <tbody>
            {settled.map((run) => {
              const durationMs = new Date(run.finished_at as string).getTime() - new Date(run.started_at as string).getTime();
              const cost =
                !walletPending && wallet ? runActualCostEur({ startedAt: run.started_at, finishedAt: run.finished_at }, wallet.hourlyPriceEur) : null;
              return (
                <tr key={run.id} className="relative border-b border-rule-soft last:border-b-0 hover:bg-wash">
                  <td className="px-5 py-2 sm:px-6">
                    <Link
                      to={`/mapari/${run.id}`}
                      className="inline-block max-w-[280px] truncate align-middle text-[13.5px] text-ink hover:underline"
                    >
                      {run.filename}
                      <span className="absolute inset-0" aria-hidden />
                    </Link>
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums text-ink-2 sm:px-6">
                    {durationMs > 0 ? formatDuration(durationMs) : '—'}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums text-ink-2 sm:px-6">
                    {cost === null ? '—' : cost === 0 ? '—' : formatCurrencyEurRo(cost)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
